import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { OpeningThread } from '@/features/assistant/AssistantComposer.js';
import type { PanelThread } from '@/features/assistant/usePanelSend.js';
import {
  createFileThread,
  fetchFileThread,
  fileChatKeys,
  type FileReference,
  type FileThreadMode,
  type FileThreadState,
} from '@/lib/api/file-chat.js';

/** The open file's edit chat, as far as this viewer knows it. */
export interface FileThread extends PanelThread, OpeningThread {
  /** False when this file gets no Assistant here; null until the lookup answers. */
  supported: boolean | null;
  mode: FileThreadMode;
  /** False only for an origin chat the caller may not read. */
  readable: boolean;
  /** Why the chat could not be found, in words the panel can show. */
  loadError: string | null;
}

/** Shown when a send would land in a chat the caller may not read. */
export const PRIVATE_CHAT_MESSAGE =
  "This file's edit chat is private to its author.";

/** The conversation the panel shows and writes into: none while it is someone else's private chat. */
function pinnedConversation(state: FileThreadState | null): string | null {
  if (state === null || !state.readable) return null;
  return state.conversationId;
}

/**
 * Finds the file's edit chat and creates it lazily, on the first message, so opening a file never
 * leaves an empty thread behind. The server answers which chat it is on every request: the chat the
 * agent made the file in, while its audience still matches the file's, or a dedicated thread.
 * Nothing is asked while `enabled` is false.
 */
export function useFileThread(
  workspaceId: string,
  file: FileReference,
  title: string,
  enabled = true,
): FileThread {
  const lookup = useQuery({
    queryKey: fileChatKeys.thread(workspaceId, file),
    queryFn: () => fetchFileThread(workspaceId, file),
    staleTime: 30_000,
    retry: false,
    enabled,
  });
  const [created, setCreated] = useState<FileThreadState | null>(null);
  const pendingCreate = useRef<FileThreadState | null>(null);
  const state = created ?? lookup.data ?? null;
  const conversationId = pinnedConversation(state);

  const ensure = useCallback(async (): Promise<string> => {
    if (conversationId) return conversationId;
    const next = await createFileThread({ workspaceId, file, title });
    const nextId = pinnedConversation(next);
    if (nextId === null) throw new Error(PRIVATE_CHAT_MESSAGE);
    pendingCreate.current = next;
    return nextId;
  }, [conversationId, workspaceId, file, title]);

  const adopt = useCallback((adoptedId: string) => {
    const pending = pendingCreate.current;
    if (pending?.conversationId !== adoptedId) return;
    setCreated(pending);
  }, []);

  const supported = lookup.isPending
    ? null
    : lookup.isError || lookup.data !== null;
  return useMemo(
    () => ({
      supported,
      ready: !lookup.isPending,
      mode: state?.mode ?? 'none',
      readable: state?.readable ?? true,
      conversationId,
      loadError: lookup.error ? lookup.error.message : null,
      ensure,
      adopt,
    }),
    [
      supported,
      lookup.isPending,
      lookup.error,
      state,
      conversationId,
      ensure,
      adopt,
    ],
  );
}
